import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGet } from '../../lib/api';
import bookingService from '../../services/bookingService';
import { useCurrency } from '../../context/CurrencyContext';
import { Coffee, Calendar, Clock, CheckCircle, CreditCard, User, Video, MapPin, Shield, Info, Loader2 } from 'lucide-react';
import InlineCurrencyPicker from '../../components/InlineCurrencyPicker';

// Fika Pricing Constants (base amounts in SEK)
const FIKA_FEE_SEK = 199;

// Get tomorrow's date as YYYY-MM-DD
const getTomorrowDate = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow.toISOString().slice(0, 10);
};

// Get minimum bookable date (tomorrow)
const getMinDate = () => getTomorrowDate();

export default function QuickFikaBooking({ creativeId: creativeIdProp, inline = false }) {
  const { t } = useTranslation();
  const params = useParams();
  const creativeId = creativeIdProp ?? params.creativeId;
  const navigate = useNavigate();
  const location = useLocation();
  
  // Form state
  const [scheduledAt, setScheduledAt] = useState('');
  const [fikaType, setFikaType] = useState('fika_online');
  const [creative, setCreative] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [needLogin, setNeedLogin] = useState(false);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [errorSlots, setErrorSlots] = useState('');
  const [weekRange, setWeekRange] = useState(2);
  const [selectedDate, setSelectedDate] = useState(getTomorrowDate());
  const [busySlots, setBusySlots] = useState([]);
  
  const loginUrl = `/login?redirect=${encodeURIComponent(location.pathname + (location.search || ''))}`;
  
  // Currency conversion hook
  const { formatPrice, convert, currency: userCurrency } = useCurrency();

  // Calculate pricing
  const creativeHourlyRate = Number(creative?.hourly_rate) || 0;
  const creativeCurrency = creative?.currency || 'SEK';
  const fikaFeeAmount = fikaType === 'fika_in_person' ? FIKA_FEE_SEK : 0;
  
  const hourlyRateInSEK = creativeCurrency === 'SEK' 
    ? creativeHourlyRate 
    : convert(creativeHourlyRate, creativeCurrency, 'SEK');
  
  const totalPriceInSEK = fikaFeeAmount + hourlyRateInSEK;
  
  const displayFikaFee = convert(fikaFeeAmount, 'SEK', userCurrency);
  const displayHourlyRate = convert(creativeHourlyRate, creativeCurrency, userCurrency);
  const displayTotalPrice = convert(totalPriceInSEK, 'SEK', userCurrency);

  // Load creative info
  useEffect(() => {
    if (!creativeId) return;
    (async () => {
      try {
        const data = await bookingService.fetchCreativeById(creativeId);
        setCreative(data);
      } catch { void 0; }
    })();
  }, [creativeId]);

  // Handle payment success redirect
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    if (params.get('paid') === '1') {
      setResult({ status: 'confirmed', message: 'Payment successful! Your Fika session is confirmed.' });
    }
    if (params.get('canceled') === '1') {
      setError('Payment was cancelled. You can try again.');
    }
  }, [location.search]);

  // Check if a time slot is busy
  const isSlotBusy = (slotStart) => {
    const slotEnd = new Date(slotStart.getTime() + 60 * 60 * 1000);
    return busySlots.some(busy => slotStart < busy.end && slotEnd > busy.start);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setResult(null);
    setLoading(true);

    try {
      let userId = null;
      try {
        const me = await apiGet('/users/me');
        userId = me?.data?.user?.id || me?.user?.id || me?.data?.id || me?.id || null;
      } catch { void 0; }
      
      if (!userId) {
        setError('Please login to book');
        setNeedLogin(true);
        setLoading(false);
        return;
      }

      if (!scheduledAt) {
        setError('Please select an available time slot');
        setLoading(false);
        return;
      }

      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      const sel = new Date(scheduledAt);
      if (sel < tomorrow) {
        setError('Bookings must be at least 1 day in advance');
        setLoading(false);
        return;
      }

      const res = await bookingService.quickPay({
        creative_id: creativeId,
        meeting_type: fikaType,
        scheduled_at: scheduledAt,
        hours: 1,
        notes: fikaType === 'fika_online' ? 'Online Fika Session' : 'In-Person Fika Meeting',
      });

      if (res?.checkout_url) {
        window.location.assign(res.checkout_url);
        return;
      }

      if (!res?.payment_required) {
        setResult({
          booking: res?.booking,
          pricing: res?.pricing,
          status: 'confirmed',
          message: 'Your Fika session is confirmed!',
        });
      } else {
        setResult({
          booking: res?.booking,
          pricing: res?.pricing,
          status: 'pending',
          message: res?.message || 'Booking created. Complete payment to confirm.',
        });
      }
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Failed to book fika');
    } finally {
      setLoading(false);
    }
  };

  // Fetch available slots and existing bookings
  useEffect(() => {
    let active = true;
    async function loadSlots() {
      if (!creativeId) return;
      setLoadingSlots(true);
      setErrorSlots('');
      try {
        const now = new Date();
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);
        const horizon = new Date(tomorrow.getTime() + (weekRange === 1 ? 7 : 14) * 24 * 60 * 60 * 1000);
        
        const res = await apiGet('/availabilities', {
          creative_id: creativeId,
          ends_after: tomorrow.toISOString(),
          starts_before: horizon.toISOString(),
          limit: 200,
        });
        const list = res?.data?.availabilities || res?.availabilities || [];
        const filtered = list
          .filter((it) => (it?.kind || 'available') === 'available')
          .map((it) => ({
            id: it.id,
            start_at: it.start_at,
            end_at: it.end_at,
          }))
          .filter((it) => new Date(it.start_at) >= tomorrow)
          .sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
        
        try {
          const bookingsRes = await apiGet('/bookings', {
            creative_id: creativeId,
            status: 'confirmed,pending',
            start_after: tomorrow.toISOString(),
            start_before: horizon.toISOString(),
            limit: 100,
          });
          const bookings = bookingsRes?.data?.bookings || bookingsRes?.bookings || [];
          if (active) {
            setBusySlots(bookings.map(b => ({
              start: new Date(b.start_at),
              end: new Date(b.end_at),
            })));
          }
        } catch (e) {
          console.error('Failed to load bookings:', e);
        }

        if (active) setSlots(filtered);
      } catch (e) {
        if (active) setErrorSlots(e?.response?.data?.error || e?.message || 'Failed to load availability');
      } finally {
        if (active) setLoadingSlots(false);
      }
    }
    loadSlots();
    return () => { active = false; };
  }, [creativeId, weekRange]);

  const FikaTypeSelector = () => (
    <div className="space-y-2">
      <label className="text-sm font-medium text-secondary-text">Fika Type</label>
      <div className="flex gap-2 sm:gap-3">
        <button
          type="button"
          onClick={() => setFikaType('fika_online')}
          className={`flex-1 flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg border transition-all text-sm ${
            fikaType === 'fika_online'
              ? 'border-blue-500 bg-blue-500/10 text-blue-400'
              : 'border-card-border bg-subtle-bg text-secondary-text hover:border-blue-400'
          }`}
        >
          <Video size={16} />
          <span>Online</span>
        </button>
        <button
          type="button"
          onClick={() => setFikaType('fika_in_person')}
          className={`flex-1 flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg border transition-all text-sm ${
            fikaType === 'fika_in_person'
              ? 'border-blue-500 bg-blue-500/10 text-blue-400'
              : 'border-card-border bg-subtle-bg text-secondary-text hover:border-blue-400'
          }`}
        >
          <MapPin size={16} />
          <span>In-Person</span>
        </button>
      </div>
    </div>
  );

  const PricingBreakdown = ({ compact = false }) => (
    <div className={`rounded-lg border border-card-border ${compact ? 'p-2.5 sm:p-3' : 'p-3 sm:p-4'} bg-subtle-bg`}>
      {/* Currency selector */}
      <div className="flex items-center justify-between mb-2 sm:mb-3 pb-2 border-b border-card-border">
        <span className="text-xs text-secondary-text">Prices shown in</span>
        <InlineCurrencyPicker />
      </div>
      <div className="space-y-1.5 sm:space-y-2">
        {fikaType === 'fika_in_person' && (
          <div className="flex justify-between items-center text-sm">
            <div className="flex items-center gap-1">
              <Coffee size={14} className="text-blue-500" />
              <span className="text-secondary-text">Fika Fee</span>
            </div>
            <span className="font-medium text-primary-text">{formatPrice(displayFikaFee, userCurrency)} {userCurrency}</span>
          </div>
        )}
        {creativeHourlyRate > 0 && (
          <div className="flex justify-between items-center text-sm">
            <div className="flex items-center gap-1">
              <span className="text-secondary-text">Session Fee</span>
              <Shield size={12} className="text-green-500" />
            </div>
            <span className="font-medium text-primary-text">{formatPrice(displayHourlyRate, userCurrency)} {userCurrency}</span>
          </div>
        )}
        <div className="border-t border-card-border pt-2 mt-2">
          <div className="flex justify-between items-center">
            <span className="font-semibold text-primary-text">Total</span>
            <span className="text-base sm:text-lg font-bold text-primary-text">{formatPrice(displayTotalPrice, userCurrency)} {userCurrency}</span>
          </div>
        </div>
      </div>
      
      {!compact && (
        <div className="mt-2 sm:mt-3 flex items-start gap-2 text-xs text-secondary-text bg-card-bg rounded p-2 border border-card-border">
          {fikaType === 'fika_in_person' ? (
            <>
              <Coffee size={12} className="mt-0.5 flex-shrink-0 text-blue-500" />
              <span>Fika fee covers refreshments. Session fee held in escrow.</span>
            </>
          ) : (
            <>
              <Video size={12} className="mt-0.5 flex-shrink-0 text-blue-500" />
              <span>Online Fika - no additional fees. Session fee held in escrow.</span>
            </>
          )}
        </div>
      )}
    </div>
  );

  const SlotGrid = () => (
    <div className="space-y-2 sm:space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <label className="text-sm font-medium text-secondary-text flex items-center gap-1.5 sm:gap-2">
          <Calendar size={14} />
          Select Time Slot
        </label>
        <div className="flex items-center gap-1 sm:gap-2">
          <button
            type="button"
            className={`px-2 sm:px-3 py-1 text-xs rounded transition-colors ${
              weekRange === 1 ? 'bg-blue-600 text-white' : 'bg-subtle-bg text-secondary-text hover:bg-card-border'
            }`}
            onClick={() => setWeekRange(1)}
          >
            1 Week
          </button>
          <button
            type="button"
            className={`px-2 sm:px-3 py-1 text-xs rounded transition-colors ${
              weekRange === 2 ? 'bg-blue-600 text-white' : 'bg-subtle-bg text-secondary-text hover:bg-card-border'
            }`}
            onClick={() => setWeekRange(2)}
          >
            2 Weeks
          </button>
        </div>
      </div>

      {loadingSlots && (
        <div className="flex items-center gap-2 text-xs sm:text-sm text-secondary-text">
          <Loader2 size={14} className="animate-spin" />
          Loading available slots...
        </div>
      )}
      {errorSlots && <div className="text-xs sm:text-sm text-red-400">{errorSlots}</div>}

      {slots.length > 0 && (
        <div className="grid grid-cols-2 gap-1.5 sm:gap-2 max-h-36 sm:max-h-48 overflow-y-auto">
          {slots.map((s) => {
            const start = new Date(s.start_at);
            const end = new Date(s.end_at);
            const dateLabel = start.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
            const timeLabel = `${start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}–${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}`;
            const selected = scheduledAt && new Date(scheduledAt).toISOString() === start.toISOString();
            const isBusy = isSlotBusy(start);
            
            return (
              <button
                key={s.id}
                type="button"
                disabled={isBusy}
                onClick={() => !isBusy && setScheduledAt(start.toISOString())}
                className={`p-1.5 sm:p-2 rounded-lg border text-xs sm:text-sm transition-all ${
                  isBusy
                    ? 'border-card-border bg-subtle-bg/50 text-secondary-text/50 cursor-not-allowed opacity-50'
                    : selected
                      ? 'border-blue-500 bg-blue-500/10 text-blue-400'
                      : 'border-card-border bg-subtle-bg text-primary-text hover:border-blue-400'
                }`}
              >
                <div className="font-medium text-xs">{dateLabel}</div>
                <div className="text-xs text-secondary-text">{isBusy ? 'Unavailable' : timeLabel}</div>
              </button>
            );
          })}
        </div>
      )}

      {!loadingSlots && slots.length === 0 && (
        <div className="space-y-2 sm:space-y-3">
          <p className="text-xs text-secondary-text">No pre-configured slots. Select a date and time:</p>
          <input
            className="input w-full text-sm"
            type="date"
            value={selectedDate}
            min={getMinDate()}
            onChange={(e) => {
              setSelectedDate(e.target.value || getTomorrowDate());
              setScheduledAt('');
            }}
          />
          {selectedDate && (
            <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
              {Array.from({ length: 8 }).map((_, i) => {
                const hour = 9 + i;
                const start = new Date(`${selectedDate}T${String(hour).padStart(2,'0')}:00:00`);
                const selected = scheduledAt && new Date(scheduledAt).toISOString() === start.toISOString();
                const timeLabel = start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
                const isBusy = isSlotBusy(start);
                
                return (
                  <button
                    key={`slot-${selectedDate}-${hour}`}
                    type="button"
                    disabled={isBusy}
                    onClick={() => !isBusy && setScheduledAt(start.toISOString())}
                    className={`py-1.5 sm:py-2 rounded border text-xs sm:text-sm transition-all ${
                      isBusy
                        ? 'border-card-border bg-subtle-bg/50 text-secondary-text/50 cursor-not-allowed line-through opacity-50'
                        : selected
                          ? 'border-blue-500 bg-blue-500/10 text-blue-400'
                          : 'border-card-border bg-subtle-bg text-primary-text hover:border-blue-400'
                    }`}
                  >
                    {timeLabel}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );

  // Success state
  if (result?.status === 'confirmed') {
    return (
      <div className={inline ? 'space-y-4' : 'container mx-auto px-4 py-6'}>
        <div className="card p-4 sm:p-6 space-y-4 text-center max-w-md mx-auto">
          <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto bg-green-500/10 rounded-full flex items-center justify-center">
            <CheckCircle size={28} className="text-green-500" />
          </div>
          <h2 className="text-lg sm:text-xl font-semibold text-primary-text">Fika Booked!</h2>
          <p className="text-sm text-secondary-text">{result.message}</p>
          {result.booking && (
            <div className="text-sm text-secondary-text space-y-1 bg-subtle-bg rounded-lg p-3 sm:p-4 border border-card-border">
              <p>Booking #: <span className="font-mono text-primary-text">{result.booking.booking_number}</span></p>
              <p>Date: <span className="text-primary-text">{new Date(result.booking.start_at).toLocaleDateString()}</span></p>
              <p>Time: <span className="text-primary-text">{new Date(result.booking.start_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span></p>
            </div>
          )}
          {result.pricing?.escrow_amount > 0 && (
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 text-sm">
              <div className="flex items-center justify-center gap-2 text-blue-400">
                <Shield size={14} />
                <span className="text-xs sm:text-sm">{formatPrice(convert(result.pricing.escrow_amount, result.pricing?.currency || 'SEK', userCurrency), userCurrency)} {userCurrency} held in escrow</span>
              </div>
            </div>
          )}
          <button
            className="btn btn-primary w-full"
            onClick={() => navigate('/dashboard/bookings')}
          >
            View My Bookings
          </button>
        </div>
      </div>
    );
  }

  if (inline) {
    return (
      <div className="space-y-3 sm:space-y-4">
        <div className="flex items-center gap-2 text-base sm:text-lg font-semibold text-primary-text">
          <Coffee size={18} className="text-blue-500" />
          {t('quickFika.title', { defaultValue: 'Quick Fika Booking' })}
        </div>
        <form onSubmit={submit} className="rounded-xl bg-card-bg border border-card-border p-3 sm:p-4 space-y-3 sm:space-y-4">
          {creative && (
            <div className="flex items-center gap-2 sm:gap-3 pb-2 sm:pb-3 border-b border-card-border">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                {creative.avatar_url ? (
                  <img src={creative.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                ) : (
                  <User size={18} className="text-white" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-primary-text text-sm truncate">{creative.display_name || creative.full_name}</div>
                <div className="text-xs text-secondary-text truncate">{creative.headline || creative.title}</div>
                {creativeHourlyRate > 0 && (
                  <div className="text-xs text-green-500">{formatPrice(displayHourlyRate, userCurrency)} {userCurrency}/hour</div>
                )}
              </div>
            </div>
          )}

          <FikaTypeSelector />
          <SlotGrid />
          
          {scheduledAt && <PricingBreakdown compact />}

          {error && <div className="text-xs sm:text-sm text-red-400 bg-red-500/10 border border-red-500/20 p-2 rounded">{error}</div>}

          <button
            type="submit"
            disabled={loading || !scheduledAt}
            className="w-full py-2.5 sm:py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <CreditCard size={16} />
                <span>Book & Pay {formatPrice(displayTotalPrice, userCurrency)} {userCurrency}</span>
              </>
            )}
          </button>

          {needLogin && (
            <button
              type="button"
              className="w-full btn btn-outline py-2.5 sm:py-3 text-sm"
              onClick={() => navigate(loginUrl)}
            >
              {t('auth.loginToContinue', { defaultValue: 'Log in to continue' })}
            </button>
          )}
        </form>
      </div>
    );
  }

  return (
    <section className="container mx-auto px-4 py-4 sm:py-6 space-y-4 sm:space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Coffee size={24} className="text-blue-500" />
          <h1 className="text-xl sm:text-2xl font-semibold text-primary-text">{t('quickFika.title', { defaultValue: 'Quick Fika Booking' })}</h1>
        </div>
        <p className="text-sm text-secondary-text">{t('quickFika.subtitle', { defaultValue: 'Schedule a quick chat over virtual or in-person coffee.' })}</p>
      </div>

      <form onSubmit={submit} className="card p-4 sm:p-6 space-y-4 sm:space-y-6 max-w-xl">
        {creative && (
          <div className="flex items-center gap-3 sm:gap-4 pb-3 sm:pb-4 border-b border-card-border">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center flex-shrink-0">
              {creative.avatar_url ? (
                <img src={creative.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
              ) : (
                <User size={24} className="text-white" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-base sm:text-lg font-medium text-primary-text truncate">{creative.display_name || creative.full_name}</div>
              <div className="text-sm text-secondary-text truncate">{creative.headline || creative.title}</div>
              {creativeHourlyRate > 0 && (
                <div className="text-sm text-green-500 font-medium">{formatPrice(displayHourlyRate, userCurrency)} {userCurrency}/hour</div>
              )}
            </div>
          </div>
        )}

        <FikaTypeSelector />
        <SlotGrid />

        {scheduledAt && (
          <div className="space-y-3 sm:space-y-4">
            <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-3 sm:p-4 space-y-2">
              <div className="text-sm font-medium text-primary-text">Selected Time</div>
              <div className="flex items-center gap-2 text-secondary-text text-sm">
                <Clock size={14} />
                <span>{new Date(scheduledAt).toLocaleString()}</span>
              </div>
            </div>
            
            <PricingBreakdown />
          </div>
        )}

        {error && <div className="text-xs sm:text-sm text-red-400 bg-red-500/10 border border-red-500/20 p-3 rounded-lg">{error}</div>}

        <button
          type="submit"
          disabled={loading || !scheduledAt}
          className="w-full py-2.5 sm:py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
        >
          {loading ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <CreditCard size={18} />
              <span>Book & Pay {formatPrice(displayTotalPrice, userCurrency)} {userCurrency}</span>
            </>
          )}
        </button>

        {needLogin && (
          <button
            type="button"
            className="w-full btn btn-outline py-2.5 sm:py-3 text-sm"
            onClick={() => navigate(loginUrl)}
          >
            {t('auth.loginToContinue', { defaultValue: 'Log in to continue' })}
          </button>
        )}
      </form>
    </section>
  );
}
